<script setup lang="ts">
// @ts-check

import CheckField from '@/components/fields/CheckField.vue'
import { useArrayMap } from '@vueuse/core'
import * as _ from 'lodash-es'
import { toRef } from 'vue'

interface Props {
  /** The options to display in the checklist */
  options: ChecklistOption[]
  /** Whether to apply hover styles to the options */
  hover?: boolean
  /** Whether to apply boxed styles to the options */
  boxed?: boolean
}

const { options } = defineProps<Props>()

const selectedValues = defineModel<unknown[]>({ required: true })

defineOptions({
  name: 'BaseChecklist'
})

const mappedOptions = useArrayMap(
  toRef(() => options),
  (option) => ({
    ...option,
    key: JSON.stringify(option.value),
    value: _.some(selectedValues.value, (value) =>
      _.isEqual(option.value, value)
    )
  })
)

const onSelectOption = ({ key }: { key: string }) => {
  let newSelectedOptions = []

  if (!selectedValues.value.some((value) => JSON.stringify(value) === key)) {
    newSelectedOptions = [...selectedValues.value, JSON.parse(key)]
  } else {
    newSelectedOptions = selectedValues.value.filter(
      (value) => JSON.stringify(value) !== key
    )
  }

  selectedValues.value = newSelectedOptions
}
</script>

<template>
  <ul class="flex flex-col">
    <li v-for="option in mappedOptions" :key="option.key">
      <CheckField
        class="px-3 py-2"
        :class="{
          'hover:bg-secondary-100': hover,
          boxed
        }"
        :model-value="option.value"
        @update:model-value="onSelectOption(option)"
      >
        {{ option.label }}
      </CheckField>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

:deep(.boxed) {
  @apply mb-4 cursor-pointer rounded-lg border border-secondary-200 bg-white;
  @apply px-2 py-4 font-semibold hover:border-primary;
}
</style>
